const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const parseFlexibleDate = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  const ddmmyyyy = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmmyyyy) {
    return new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1]));
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

export const DARWIN_RECORD_MONTH_KEY = 'darwinRecord.selectedMonth';

export const readStoredMonth = () => {
  try {
    return sessionStorage.getItem(DARWIN_RECORD_MONTH_KEY) || '';
  } catch (error) {
    return '';
  }
};

export const writeStoredMonth = (monthId) => {
  try {
    if (monthId) {
      sessionStorage.setItem(DARWIN_RECORD_MONTH_KEY, monthId);
    } else {
      sessionStorage.removeItem(DARWIN_RECORD_MONTH_KEY);
    }
  } catch (error) {
    // Ignore quota / private-mode failures.
  }
};

/** True when the record date is after today. */
export const isFutureRecordDate = (value, today = new Date()) => {
  const date = parseFlexibleDate(value);
  if (!date) return false;
  return startOfDay(date) > startOfDay(today);
};

/** True when the record date is today. */
export const isTodayRecordDate = (value, today = new Date()) => {
  const date = parseFlexibleDate(value);
  if (!date) return false;
  return startOfDay(date).getTime() === startOfDay(today).getTime();
};

/** Mark regularization is past dates only — not today and not future. */
export const canMarkRegularization = (value, today = new Date()) =>
  Boolean(parseFlexibleDate(value)) &&
  !isFutureRecordDate(value, today) &&
  !isTodayRecordDate(value, today);

export const formatDateParts = (value, fallbackDay = '', fallbackMonth = '') => {
  const date = parseFlexibleDate(value);
  if (!date) {
    return {
      dd: fallbackDay,
      mmm: fallbackMonth,
      ddd: '',
    };
  }
  return {
    dd: String(date.getDate()).padStart(2, '0'),
    mmm: MONTH_NAMES[date.getMonth()],
    ddd: DAY_NAMES[date.getDay()],
  };
};

export const formatDisplayDate = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return value || '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getFullYear()}`;
};

export const formatHeaderDate = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return value || '—';
  const dd = String(date.getDate()).padStart(2, '0');
  return `${dd}-${MONTH_NAMES[date.getMonth()]}-${date.getFullYear()}, ${DAY_NAMES[date.getDay()]}`;
};

export const toIsoDate = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const toDisplayDate = (value) => {
  const date = parseFlexibleDate(value);
  if (!date) return value || '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${date.getFullYear()}`;
};

export const formatTime = (value) => {
  if (!value) return 'N/A';
  const timeMatch = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    let hours = Number(timeMatch[1]);
    const minutes = timeMatch[2];
    const suffix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${String(hours).padStart(2, '0')}:${minutes} ${suffix}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${minutes} ${suffix}`;
};

export const getRecordStatusClass = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (status === 'Present' || status === 'Holiday') return 'darwin-status-present';
  if (status === 'Weekly Off' || normalized === 'weekly off') return 'darwin-status-weekly-off';
  if (status === 'Absent') return 'darwin-status-absent';
  if (normalized === 'regularization pending') return 'darwin-status-reg-pending';
  if (status === 'N/A' || normalized === 'n.a.') return 'darwin-status-na';
  return 'darwin-status-default';
};

export const placeholderToFilterKey = (placeHolder = '') => {
  const key = placeHolder.replaceAll(' ', '_').toLowerCase();
  if (key === 'requestor_email') return 'requestor_email';
  if (key === 'request_type') return 'request_type';
  if (key === 'regularization_start_date') return 'start_date';
  if (key === 'regularization_end_date') return 'end_date';
  return null;
};

export const buildQueryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export const darwinRecordListPath = (baseUrl, monthId = readStoredMonth()) =>
  `${baseUrl}/darwin-record${buildQueryString({ month: monthId })}`;

export const parseQueryParams = (search) => {
  const params = new URLSearchParams(search || '');
  const result = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};
